import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Stack,
  CircularProgress,
  Link,
  TextField,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useSelector } from "react-redux";
import PageLoading from "@/app/components/shared/pageLoading";
import dataService from "@/app/services/dataService";
import { RootState } from "@/app/store/store";
import HomepageSecondaryUser from "../home/homepageSecondaryUser";
import {
  CustomerAccountRequest,
  CustomerAccountResponse,
} from "@/app/models/accountProfile/types";
import checkIcon from "../../../img/check-icon-green.png";
import BorderedInputLansing from "@/app/components/shared/BorderedInput";
import { useNavigate } from "react-router-dom";

export default function CustomerAccount() {
  const user = useSelector((s: RootState) => s.userState.user ?? 0);
  const isSecondaryUser = useSelector(
    (s: RootState) => s.userState.user.isSecondaryUser
  );

  const navigate = useNavigate();

  const isPrimary = !isSecondaryUser && !user.isAdmin;
  const [pageLoading, setPageLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [account, setAccount] = useState<CustomerAccountResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showReasonError, setShowReasonError] = useState(false);

  const [infoOpen, setInfoOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);

  const [company, setCompany] = useState("");
  const [branch, setBranch] = useState("");
  const [companyEmail, setCompanyEmail] = useState("");
  const [street1, setStreet1] = useState("");
  const [street2, setStreet2] = useState("");
  const [city, setCity] = useState("");
  const [stateProvince, setStateProvince] = useState("");
  const [zip, setZip] = useState("");

  const [seeded, setSeeded] = useState(false);
  const initialStreet1Ref = React.useRef<string>("");
  const initialStreet2Ref = React.useRef<string>("");
  const initialCityRef = React.useRef<string>("");
  const initialStateProvinceRef = React.useRef<string>("");
  const initialZipRef = React.useRef<string>("");

  const hasAddressChanged =
    seeded &&
    ((street1 ?? "") !== (initialStreet1Ref.current ?? "") ||
      (zip ?? "") !== (initialZipRef.current ?? "") ||
      (city ?? "") !== (initialCityRef.current ?? "") ||
      String(stateProvince ?? "") !==
        String(initialStateProvinceRef.current ?? "") ||
      (street2 ?? "") !== (initialStreet2Ref.current ?? ""));

  const [addressChangeReason, setAddressChangeReason] = useState<string>("");
  const [notes, setNotes] = useState("");
  const [showNotesError, setShowNotesError] = useState(false); // only flips on bad submit
  const isNotesRequired = addressChangeReason === "Other";
  const notesIsEmpty = !notes.trim();
  const notesError = showNotesError && isNotesRequired && notesIsEmpty;

  useEffect(() => {
    if (!hasAddressChanged) {
      setAddressChangeReason("");
    }
  }, [hasAddressChanged]);

  useEffect(() => {
    if (infoOpen && account) {
      const selectedWarehouse = account.availableWarehouses?.find(
        (w: any) => w?.selected
      );
      const selectedState = account.availableStates?.find(
        (s: any) => s?.selected
      );

      const seededBranch = String(
        selectedWarehouse?.value ?? account.branch ?? ""
      );
      const seededStateProv = String(
        selectedState?.value ?? account.stateProvince ?? ""
      );
      const seededStreet1 = account.streetAddress ?? "";
      const seededStreet2 = account.streetAddress2 ?? "";
      const seededCity = account.city ?? "";
      const seededZip = account.zipPostalCode ?? "";

      setCompany(account.company ?? "");
      setBranch(seededBranch);
      setCompanyEmail(account.companyEmail ?? "");
      setStreet1(seededStreet1);
      setStreet2(seededStreet2);
      setCity(seededCity);
      setStateProvince(seededStateProv);
      setZip(seededZip);

      initialStreet1Ref.current = seededStreet1;
      initialStreet2Ref.current = seededStreet2;
      initialCityRef.current = seededCity;
      initialStateProvinceRef.current = seededStateProv;
      initialZipRef.current = seededZip;

      setSeeded(true);
      setShowNotesError(false);
      setAddressChangeReason("");
    } else {
      setSeeded(false);
    }
  }, [infoOpen, account]);

  const handleSubmit = async () => {
    try {
      setLoading(true);

      if (hasAddressChanged && !addressChangeReason) {
        setShowReasonError(true);
        setLoading(false);
        return;
      }

      if (isNotesRequired && notesIsEmpty) {
        setShowNotesError(true);
        setLoading(false);
        return;
      }

      const payload: CustomerAccountRequest = {
        note: notes,
        customerNumber: account?.accountNumber ?? "",
        companyName: company,
        branch:
          account?.availableWarehouses?.find((x) => x.value === branch)?.text ??
          "",
        companyEmail,
        streetAddress: street1,
        streetAddress2: street2,
        zip,
        city,
        state:
          account?.availableStates?.find((x) => x.value === stateProvince)
            ?.text ?? "",
        reasonForAddressChange: addressChangeReason,
      };

      const res = await dataService.updateCustomerAccount(payload);
      if (res.success) {
        setSuccessOpen(true);
        setInfoOpen(false);
      }
    } catch (e: any) {
      setError(
        e?.response?.data?.message ||
          e?.message ||
          "Failed to submit change request."
      );
    } finally {
      setLoading(false);
    }
  };

  const loadAccount = useCallback(async () => {
    setPageLoading(true);
    setError(null);
    try {
      const res = await dataService.getCustomerAccount();
      if (res?.success && res.data) {
        setAccount(res.data as CustomerAccountResponse);
      } else if (res?.error) {
        setError(res.error);
      }
    } catch (e: any) {
      setError(
        e?.response?.data?.message ||
          e?.message ||
          "Failed to load customer account."
      );
    } finally {
      setPageLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await loadAccount();
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
  }, [loadAccount]);

  if (pageLoading) return <PageLoading />;

  const display = (v?: string | null) => (v ?? "").trim();

  return (
    <>
      {loading ? (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            bgcolor: "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
            pointerEvents: "auto",
          }}
        >
          <CircularProgress size={40} sx={{ color: "#1976d2" }} />
        </Box>
      ) : null}

      <Box sx={{ pb: 2, minHeight: "100vh" }}>
        {isSecondaryUser && <HomepageSecondaryUser />}

        {/* Header */}
        <Box
          sx={{
            backgroundColor: "#2C7EAC",
            marginBottom: "50px",
            minHeight: "100px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              color: "#fff",
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: 600,
              fontSize: "36px",
            }}
          >
            Customer Account
          </Typography>
        </Box>

        <Box
          sx={{
            mt: 4,
            px: 4,
            mx: "auto",
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} md={5.5}>
              <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                Lansing Customer Account
              </Typography>
              <Typography sx={{ mt: 2 }}>
                If you have questions or would like to make changes to your
                Lansing customer account, please call{" "}
                <span style={{ fontWeight: "bold", whiteSpace: "nowrap" }}>
                  800-868-8779.
                </span>
              </Typography>

              {error ? (
                <Typography color="error" textAlign="center">
                  {error}
                </Typography>
              ) : (
                <Stack alignItems="center" width={"100%"} sx={{ mt: 5 }}>
                  <ProfileRow
                    label="Account Number:"
                    value={display(
                      account?.isSapSource
                        ? account?.sapCustomerNumber
                        : account?.accountNumber || ""
                    )}
                  />
                  <ProfileRow
                    label="Company Name:"
                    value={display(account?.company)}
                  />
                  <ProfileRow
                    label="Branch:"
                    value={display(account?.branch)}
                  />
                  <ProfileRow
                    label="Company Email:"
                    value={display(account?.companyEmail)}
                  />
                  <ProfileRow
                    label="Street Address:"
                    value={display(account?.streetAddress)}
                  />
                  <ProfileRow
                    label="Street Address 2:"
                    value={display(account?.streetAddress2)}
                  />
                  <ProfileRow label="City:" value={display(account?.city)} />
                  <ProfileRow
                    label="State:"
                    value={display(
                      account?.availableStates?.find((s) => s.selected)?.text ??
                        account?.stateProvince
                    )}
                  />
                  <ProfileRow
                    label="Zip Code:"
                    value={display(account?.zipPostalCode)}
                  />
                </Stack>
              )}
            </Grid>
            <Grid item xs={12} md={0.5}></Grid>
            <Grid item xs={12} md={6}>
              <>
                <Typography sx={{ fontWeight: "bold", mt: 1 }}>
                  Looking for your LansingNOW account information?
                </Typography>
                <Typography sx={{ mt: 2 }}>
                  Click{" "}
                  <Link
                    component="button"
                    underline="hover"
                    sx={{
                      fontWeight: 600,
                      color: "#0057B8",
                      cursor: "pointer",
                      display: "inline",
                      verticalAlign: "baseline",
                    }}
                    onClick={() => navigate("/profile")}
                    // onClick={() => setInfoOpen(true)}
                  >
                    here
                  </Link>{" "}
                  to manage your online LansingNOW account.
                </Typography>
              </>
            </Grid>
          </Grid>
        </Box>
      </Box>

      <Dialog
        open={successOpen}
        onClose={() => setSuccessOpen(false)}
        fullWidth
        maxWidth="sm"
        scroll="body"
      >
        <DialogTitle sx={{ p: 0 }}>
          <IconButton
            aria-label="close"
            onClick={() => setSuccessOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            overflow: "visible",
            pt: 4,
            pb: 6,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          <Typography
            component="h1"
            sx={{
              fontSize: 28,
              fontWeight: 600,
              color: "#494440",
              fontFamily: '"Montserrat", Verdana, sans-serif',
              mb: 3,
              px: 2,
              mt: 4,
            }}
          >
            Your request has been submitted
            <br />
            successfully!
          </Typography>
          <img
            src={checkIcon}
            alt="Success"
            style={{ width: 150, height: 150 }}
          />
          <Typography
            component="h1"
            sx={{
              fontFamily: '"Montserrat", Verdana, sans-serif',
              fontSize: 18,
              fontWeight: 600,
              color: "#494440",
            }}
          >
            We will review this request shortly.
          </Typography>
        </DialogContent>
      </Dialog>

      <Dialog
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        fullWidth
        maxWidth="sm"
        scroll="body"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ textAlign: "center", mt: 4 }}>
          <Typography
            sx={{
              fontWeight: 700,
              textTransform: "uppercase",
              fontFamily: '"Montserrat", Verdana, sans-serif',
            }}
          >
            Customer Account
          </Typography>
          <Typography
            sx={{
              mb: 1.5,
              fontStyle: "italic",
              color: "text.secondary",
              fontSize: "14px",
              fontFamily: '"Montserrat", Verdana, sans-serif',
            }}
          >
            Please fill out fields to request changes from the credit team.
          </Typography>

          <IconButton
            aria-label="close"
            onClick={() => setInfoOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 2, pb: 0 }}>
          <Box>
            <Typography
              variant="body2"
              sx={{ fontWeight: "600", mb: 1, color: "#494440" }}
            >
              Account Number:
            </Typography>
            <Typography sx={{ color: "#494440" }}>
              {account?.isSapSource
                ? account?.sapCustomerNumber
                : account?.accountNumber || ""}
            </Typography>

            {/* FORM */}
            <Box sx={{ my: 2 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: "600", mb: 1, color: "#494440" }}
              >
                Company Name:
              </Typography>
              <Box>
                <BorderedInputLansing
                  name="companyName"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                />
              </Box>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: "600", mb: 1, color: "#494440" }}
              >
                Branch:
              </Typography>
              <TextField
                sx={{
                  flex: 1,
                  height: "36px",
                  borderRadius: "4px",
                  backgroundColor: "transparent",
                  color: "#494440",
                  textTransform: "none",
                  fontWeight: 500,
                  borderColor: "#d9d9d9",
                  justifyContent: "start",
                }}
                InputProps={{
                  sx: { fontSize: "14px" },
                }}
                select
                size="small"
                fullWidth
                value={branch}
                onChange={(e) => setBranch(e.target.value)}
              >
                {account?.availableWarehouses?.map((w) => (
                  <MenuItem key={w.value} value={w.value}>
                    {w.text}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: "600", mb: 1, color: "#494440" }}
              >
                Company Email:
              </Typography>
              <BorderedInputLansing
                name="companyEmail"
                value={companyEmail}
                onChange={(e) => setCompanyEmail(e.target.value)}
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: "600", mb: 1, color: "#494440" }}
              >
                Street Address:
              </Typography>
              <BorderedInputLansing
                name="streetAddress"
                value={street1}
                onChange={(e) => setStreet1(e.target.value)}
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: "600", mb: 1, color: "#494440" }}
              >
                Street Address 2:
              </Typography>
              <BorderedInputLansing
                name="streetAddress2"
                value={street2}
                onChange={(e) => setStreet2(e.target.value)}
              />
            </Box>

            {hasAddressChanged && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: "600",
                    mb: 1,
                    color: "#494440",
                  }}
                >
                  Reason for Address Change:
                  <Box
                    component="span"
                    sx={{
                      ml: 0.5,
                      color: showReasonError ? "error.main" : "red",
                    }}
                  >
                    *
                  </Box>
                </Typography>

                <TextField
                  InputProps={{
                    sx: { fontSize: "14px" },
                  }}
                  select
                  size="small"
                  fullWidth
                  value={addressChangeReason}
                  onChange={(e) => {
                    const val = e.target.value;
                    setAddressChangeReason(val);
                    if (val) setShowReasonError(false);
                    if (val !== "Other") setShowNotesError(false);
                  }}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (selected) => {
                      if (!selected) {
                        return (
                          <span style={{ color: "#9e9e9e" }}>
                            Reason for address change
                          </span>
                        );
                      }
                      const opt = account?.addressChangeReasonOptions?.find(
                        (x) => String(x.value) === String(selected)
                      );
                      return opt?.text ?? "";
                    },
                    MenuProps: {
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                    },
                  }}
                >
                  {account?.addressChangeReasonOptions?.map((opt) => (
                    <MenuItem
                      key={opt.value}
                      value={String(opt.value)}
                      sx={{ fontSize: "14px" }}
                    >
                      {opt.text}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            )}

            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: "600", mb: 1, color: "#494440" }}
              >
                City:
              </Typography>
              <BorderedInputLansing
                name="city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: "600", mb: 1, color: "#494440" }}
              >
                State:
              </Typography>

              <TextField
                sx={{
                  flex: 1,
                  height: "36px",
                  borderRadius: "4px",
                  backgroundColor: "transparent",
                  color: "#494440",
                  textTransform: "none",
                  fontWeight: 500,
                  borderColor: "#d9d9d9",
                  justifyContent: "start",
                }}
                InputProps={{
                  sx: { fontSize: "14px" },
                }}
                select
                size="small"
                fullWidth
                value={stateProvince}
                onChange={(e) => setStateProvince(e.target.value)}
              >
                {account?.availableStates?.map((s) => (
                  <MenuItem key={s.value} value={String(s.value)}>
                    {s.text}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: "600", mb: 1, color: "#494440" }}
              >
                Zip Code:
              </Typography>
              <BorderedInputLansing
                name="zip"
                value={zip}
                onChange={(e) => setZip(e.target.value)}
              />
            </Box>

            <Box>
              <Typography
                sx={{
                  fontWeight: "600",
                  mb: 1,
                  fontSize: "14px",
                  color: "#494440",
                  // color: notesError ? "#62B5E5" : "#62B5E5",
                }}
              >
                Additional Requests
                {isNotesRequired && (
                  <Box
                    component="span"
                    sx={{
                      ml: 0.5,
                      color: notesError ? "#62B5E5" : "red",
                    }}
                  >
                    *
                  </Box>
                )}
              </Typography>

              <TextField
                multiline
                minRows={5}
                fullWidth
                value={notes}
                InputProps={{
                  sx: { fontSize: "14px" },
                }}
                onChange={(e) => {
                  const v = e.target.value;
                  setNotes(v);
                  if (showNotesError && v.trim()) setShowNotesError(false);
                }}
                error={notesError}
                helperText={
                  notesError ? "Please provide details for 'Other'." : ""
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: notesError ? "error.main" : "#ced4da",
                    },
                    "&:hover fieldset": {
                      borderColor: notesError ? "error.main" : "#62B5E5",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: notesError ? "error.main" : "#62B5E5",
                    },
                  },
                }}
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "flex-end", px: 3, py: 3 }}>
          <Button
            onClick={() => setInfoOpen(false)}
            className="whiteButtonOutline"
            sx={{ mr: 1.5 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            className="blueButtonOutline filledBlueButton"
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function ProfileRow({ label, value }: { label: string; value: string }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexDirection: { xs: "column", sm: "column", md: "row" },
        mb: 2,
        pb: 2,
        width: "100%",
        borderBottom: 1,
        borderColor: "#ebebeb",
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{
          textAlign: { xs: "center", sm: "center", md: "left" },
          fontSize: 14,
          color: "#494440",
          fontWeight: 600,
          fontFamily: '"Montserrat", Verdana, sans-serif',
          mr: 2,
          width: { xs: "100%", sm: "100%", md: "50%" },
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          textAlign: { xs: "center", sm: "center", md: "left" },
          fontSize: 14,
          color: "#494440",
          flex: 1,
          fontFamily: '"Montserrat", Verdana, sans-serif',
          width: { xs: "100%", sm: "100%", md: "50%" },
          wordBreak: "break-all",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}
